/*
 * The Home area's account screens (audit Phase 3.3). Replaces
 * wwwroot/js/Home/ChangePassword.js and wwwroot/js/Home/ThemeSync.js on the
 * Phase 3.1 core. No jQuery.
 *
 * Home.TopMenu -- the rail -- lives in core/nav and is registered under the
 * same root; this module is only the Manage Account page and the theme write-
 * back that follows the picker wherever it is used.
 */
import { ajaxSwap } from '../core/ajax';
import { one } from '../core/dom';
import { serialize } from '../core/forms';
import { request, url } from '../core/http';
import { attach, isValid } from '../core/validation';

const FORM = 'ChangePasswordForm';

export const ChangePassword = {
    /**
     * Post the change. The response is the Manage view again -- the server
     * renders both the success banner and the ModelState errors into it -- so
     * this is a swap, not a JSON write.
     */
    Post(): void {
        const form = one(`#${FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        void ajaxSwap({
            container: 'ViewContainer',
            url: form.getAttribute('action') || 'Home/ChangePassword',
            method: 'POST',
            data: serialize(form),
            alertInto: 'ManageMessageContainer',
            alertTitle: 'Failed to change password',
        });
    },
};

/**
 * Prepare the Manage Account form wherever it lands -- first paint or a swap
 * back after a refusal. Two things, both of which used to live in an inline
 * `@section scripts`-less `<script>` at the bottom of the view (which the SPA
 * shell does not execute when the partial is swapped in):
 *
 *  - Clear the browser's autofill. Chrome honours autocomplete="current-
 *    password" and pre-fills the SAVED sign-in password, which on a change-
 *    password form reads as "the old password is already right".
 *  - Attach validation and take over Enter, which would otherwise submit the
 *    form as a full page POST and leave the console shell behind.
 */
export function enhanceChangePasswordForm(root: ParentNode): void {
    const form = root.querySelector<HTMLFormElement>(`#${FORM}`);
    if (!form || form.dataset['mcpChangePassword'] === '1') return;
    form.dataset['mcpChangePassword'] = '1';
    // Twice, a frame apart. Chrome fills a password field asynchronously and
    // often AFTER this runs, so a single pass leaves the saved sign-in password
    // sitting in "Old password" -- which reads as "the old password is already
    // right" on the one form where that is never true. A browser pass caught
    // exactly that: the field came back 15 characters long.
    const clear = (): void => {
        for (const input of Array.from(form.querySelectorAll<HTMLInputElement>('input[type=password]'))) {
            input.value = '';
        }
    };
    clear();
    requestAnimationFrame(clear);
    window.setTimeout(clear, 250);
    attach(form);
    form.addEventListener('submit', event => {
        event.preventDefault();
        ChangePassword.Post();
    });
}

// ---------------------------------------------------------------- theme sync

let themeSyncBound = false;

/**
 * Persist the theme the picker just applied.
 *
 * Core.Web's theme.js dispatches `mcp-theme-change` after every setColor /
 * clearColor / setMode / toggleMode, and HomeController.UserTheme writes it as
 * two PersonSetting rows keyed on the signed-in user plus the McpAdmin
 * Application Resource.
 *
 * Anonymous pages emit no userTheme block, so the listener is a no-op for
 * them; the picker still writes localStorage and applies the attributes
 * locally. Fire and forget: a failed write is logged, never surfaced. The next
 * picker action retries it, and the next sign-in server-renders from
 * PersonSetting again.
 */
export function initThemeSync(): void {
    if (themeSyncBound) return;
    themeSyncBound = true;
    document.addEventListener('mcp-theme-change', event => {
        const userTheme = window.mcpAdmin?.userTheme;
        if (userTheme === null || userTheme === undefined) return;
        const detail = (event as CustomEvent<{ color?: string; mode?: string }>).detail ?? {};
        void request('Home/UserTheme', {
            method: 'POST',
            data: { color: detail.color || '', mode: detail.mode || '' },
        }).catch(error => {
            // A 401 here only means the cookie expired since page load; the
            // next navigation redirects to sign-in.
            console.warn('Theme sync failed', url('Home/UserTheme'), error);
        });
    });
}
